import { useCallback, useEffect, useMemo, useState } from 'react';
import { isTypingTarget } from '../keyboard';
import { inlineKeys } from '../keys';
import { useKeys } from '../keyScope';
import { useTaskSelection } from '../useTaskSelection';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QueryState } from './QueryState';
import { GoalCallouts } from './GoalCallouts';
import { goalColorMap, GOAL_COLORS } from '../colors';
import { MenuSurface, useMenuAnchor, type MenuItem } from './MenuSurface';
import { Icon } from './Icon';
import { ProgressBar as SharedProgressBar } from './ProgressBar';
import {
  type GoalMomentum,
  getGoalTree,
  createGoal,
  updateGoal,
  moveGoal,
  deleteGoal,
  listTasks,
  ApiError,
  type GoalTreeNode,
  type GoalPace,
  type GoalStatus,
  verdictTone,
} from '../api';
import { verdictWord } from '../habitVocab';
import { Select } from './Select';
import { ColorPicker } from './ColorPicker';
import { goalOptions, renderGoalOption } from './pickerOptions';
import { NO_NEXT_STEP_LABEL } from '../goalVocab';
import { useAccountDay } from '../accountDay';
import { habitFigure } from '../habitFigure';

/**
 * The goals screen.
 *
 * Progress alone cannot tell you whether a goal will make it — 40% is excellent in week
 * one and a disaster in the final week — so every goal with a target date also carries a
 * pace verdict, and every goal with nothing open under it says so. Those two signals are
 * the difference between a list of aspirations and something you can steer by.
 */

const PACE_LABEL: Record<GoalPace['state'], string> = {
  no_target: '',
  done: 'done',
  overdue: 'overdue',
  behind: 'behind',
  on_track: 'on track',
  ahead: 'ahead',
  /*
    Empty, so the tree draws no badge at all for a habit-carried goal — which is what the Phase
    38 fix already did by suppressing the badge locally. The suppression now comes from the
    verdict itself, so every other reader of `pace.state` gets the same answer.
  */
  not_judged: '',
};

const PACE_TONE: Record<GoalPace['state'], string> = {
  no_target: '',
  done: 'ok',
  overdue: 'bad',
  behind: 'warn',
  on_track: 'ok',
  not_judged: '',
  ahead: 'ok',
};

const STATUS_LABEL: Record<GoalStatus, string> = {
  active: 'Active',
  paused: 'Paused',
  done: 'Done',
  archived: 'Archived',
};

/** "in 12 days" / "3 days late" — a count of days is easier to act on than a date. */
function deadlineText(pace: GoalPace): string | null {
  if (pace.daysRemaining === null) return null;
  const days = pace.daysRemaining;
  if (days < 0) return `${-days} day${days === -1 ? '' : 's'} late`;
  if (days === 0) return 'due today';
  if (days === 1) return 'due tomorrow';
  return `${days} days left`;
}

function PaceBadge({ pace }: { pace: GoalPace }) {
  if (pace.state === 'no_target') return null;
  const deadline = deadlineText(pace);
  // The delta is the reason for the verdict, so it goes in the tooltip rather than being
  // left as something the user has to infer from two percentages.
  const delta =
    pace.deltaRatio === null
      ? ''
      : ` · ${pace.deltaRatio >= 0 ? '+' : ''}${Math.round(pace.deltaRatio * 100)} points vs the straight line`;

  return (
    <span
      className={`pill ${PACE_TONE[pace.state]}`}
      data-testid="goal-pace"
      data-pace-state={pace.state}
      title={`${PACE_LABEL[pace.state]}${delta}`}
    >
      {PACE_LABEL[pace.state]}
      {deadline && pace.state !== 'done' ? ` · ${deadline}` : ''}
    </span>
  );
}

/**
 * Whether the goal is still moving.
 *
 * The badge a goal without a target date can actually carry. `PaceBadge` renders nothing for
 * `no_target`, which on the account this was built against is every goal there is — so the row
 * showed a percentage and, if the goal had run out of work, a warning. A goal with ten open tasks
 * that nobody has touched in two months got nothing at all, and that is the same death.
 *
 * A fact, not a telling-off: it says when the goal last moved and stops there. `moving` shows
 * nothing, because "this is fine" is not worth a pill on every row.
 */
function MomentumBadge({ momentum }: { momentum: GoalMomentum }) {
  if (momentum.state === 'none' || momentum.state === 'moving') return null;
  const days = momentum.daysSinceMoved ?? 0;
  const what = momentum.hasEverMoved ? 'since anything was finished' : 'since it was created';

  return (
    <span
      className={`pill ${momentum.state === 'dormant' ? 'warn' : ''}`}
      data-testid="goal-momentum"
      data-momentum-state={momentum.state}
      title={`${days} days ${what}`}
    >
      {momentum.state === 'dormant' ? 'dormant' : 'slowing'} · {days}d
    </span>
  );
}

/*
  The bar is a *subtree* percentage measured in points; the count beside it on the row is a
  *direct* task count. A row reading "0 tasks" next to "9%" is both numbers telling the truth about
  different things, with nothing saying so — so each now states its own scope. The old tooltip made
  it worse by describing the bar as "N of M direct tasks done", which is neither the right scope
  nor the right unit.
*/
function ProgressBar({
  percent,
  done,
  total,
  parked,
  rollsUp,
}: {
  percent: number;
  done: number;
  total: number;
  /** Inside `total` — the bar counts the shelf — and said apart, never as open. */
  parked: number;
  /** Whether the figure covers sub-goals, i.e. whether it answers a different question from the count. */
  rollsUp: boolean;
}) {
  return (
    <SharedProgressBar
      percent={percent}
      {...(rollsUp ? { scope: 'incl. sub-goals' } : {})}
      title={`${percent}% by points, including sub-goals — ${done} of ${total} tasks on this goal itself are done${parked > 0 ? `, ${parked} parked` : ''}`}
    />
  );
}

/**
 * The one place a target date is set.
 *
 * `target_date` has existed in the schema, the API and the MCP tools since Phase 1; the
 * web UI simply never offered it, so every goal in the running deployment had a progress
 * bar and no deadline to measure it against.
 */
function GoalEditForm({ node, onDone }: { node: GoalTreeNode; onDone: () => void }) {
  const goal = node.goal;
  const [title, setTitle] = useState(goal.title);
  const [why, setWhy] = useState(goal.descriptionMd);
  const [status, setStatus] = useState<GoalStatus>(goal.status);
  // Empty means auto — which is a real, deterministic colour, not grey.
  const [color, setColor] = useState(
    (GOAL_COLORS as readonly string[]).includes(goal.color ?? '') ? goal.color! : '',
  );
  // <input type="date"> speaks YYYY-MM-DD; the column is a timestamp.
  const [target, setTarget] = useState(goal.targetDate ? goal.targetDate.slice(0, 10) : '');

  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: () =>
      updateGoal(goal.id, {
        title: title.trim(),
        descriptionMd: why,
        status,
        // Midday rather than midnight: a date-only target read back in any timezone
        // still lands on the day the user picked.
        targetDate: target ? new Date(`${target}T12:00:00Z`).toISOString() : null,
        color: color === '' ? null : color,
      }),
    onSuccess: () => {
      onDone();
      void queryClient.invalidateQueries();
    },
  });

  return (
    <form
      className="goal-edit"
      onSubmit={(e) => {
        e.preventDefault();
        if (title.trim()) save.mutate();
      }}
    >
      <div className="fields">
        <label>
          Title
          <input value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label>
          Target date
          <input type="date" value={target} onChange={(e) => setTarget(e.target.value)} />
        </label>
        <label>
          Status
          <select value={status} onChange={(e) => setStatus(e.target.value as GoalStatus)}>
            {(Object.keys(STATUS_LABEL) as GoalStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
        {/*
          Five swatches and an Auto — the shared component, not a second copy of it.

          Not a free colour picker: `input[type=color]` produces mud, produces values the token
          test cannot see, and freezes an unthemed literal into the row — the 32-character column
          has happily stored `var(--bad)` and `#fbfbfa`, the latter identical to the page
          background. Auto is the default and is never grey.

          This was hand-rolled from `.check` labels and a `.goal-swatch`, wearing the same
          `.color-picker` class as the real thing, and `.fields label` then stacked each radio over
          its swatch — so the goal editor was the third geometry the reviewer measured for one
          class in one page load. A component reused is a component that stays fixed.
          `''` is this form's "auto"; the picker's is `null`.
        */}
        <ColorPicker
          name={`color-${goal.id}`}
          label="Colour"
          value={color === '' ? null : color}
          onChange={(next) => setColor(next ?? '')}
        />
      </div>

      <label className="subtext" htmlFor={`why-${goal.id}`}>
        Why this matters — the sentence you will want to reread in month three
      </label>
      <textarea
        id={`why-${goal.id}`}
        rows={2}
        value={why}
        onChange={(e) => setWhy(e.target.value)}
        placeholder="So that…"
      />

      {save.error instanceof ApiError && <p className="banner">{save.error.message}</p>}

      <div className="detail-foot">
        <button type="submit" className="primary" disabled={!title.trim() || save.isPending}>
          Save
        </button>
        <button type="button" className="secondary" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * Re-parenting a goal.
 *
 * Goal hierarchies are discovered, not designed: you realise "run a Spartan trifecta" belongs
 * under "get fit" only once both exist. Until now that was impossible from the app — the route
 * has been there since Phase 1 with no caller — so the only way to restructure was to delete a
 * goal and rebuild it, losing its target date, its description and its tasks' attachments.
 *
 * The server refuses a cycle and enforces the five-level limit; this offers the legal choices
 * rather than letting someone find the refusal by hitting it. A goal cannot become a child of
 * itself or of its own descendant, since both would detach the subtree from the tree entirely.
 */
function MoveGoalForm({ node, onDone }: { node: GoalTreeNode; onDone: () => void }) {
  const queryClient = useQueryClient();
  const tree = useQuery({ queryKey: ['goals', 'tree'], queryFn: getGoalTree });
  const [parentId, setParentId] = useState(node.goal.parentGoalId ?? '');
  const [error, setError] = useState<string | null>(null);

  const move = useMutation({
    mutationFn: () => moveGoal(node.goal.id, parentId === '' ? null : parentId),
    onSuccess: () => {
      onDone();
      void queryClient.invalidateQueries();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not move that goal.'),
  });

  // Anything under this goal is excluded: a goal cannot be its own descendant's child.
  const descendantIds = new Set<string>();
  const collect = (n: GoalTreeNode) => {
    descendantIds.add(n.goal.id);
    n.children.forEach(collect);
  };
  collect(node);

  // A goal cannot move under itself or anything beneath it, so those are removed rather than
  // offered and then refused.
  const options = goalOptions(tree.data ?? []).filter((option) => !descendantIds.has(option.value));

  return (
    <form
      className="goal-edit"
      onSubmit={(e) => {
        e.preventDefault();
        move.mutate();
      }}
    >
      <label className="subtext">
        Move “{node.goal.title}” under
        <Select
          options={options}
          value={parentId || null}
          onChange={(next) => setParentId(next ?? '')}
          placeholder="Nothing — make it a top-level goal"
          emptyLabel="Nothing — make it a top-level goal"
          ariaLabel={`New parent for ${node.goal.title}`}
          renderOption={renderGoalOption}
        />
      </label>

      {error && <p className="error">{error}</p>}

      <div className="detail-foot">
        <button type="submit" className="primary" disabled={move.isPending}>
          Move
        </button>
        <button type="button" className="secondary" onClick={onDone}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * The four verbs that used to sit on every goal row.
 *
 * `n tasks · edit · move · + sub-goal · delete` was five text controls repeated down the whole
 * tree — the same weight the task rows carried before Phase 62 moved theirs to a command bar. The
 * count stays on the row, because it is information as well as the disclosure for the task list.
 * The verbs are here, behind `⋯` and behind a right-click.
 *
 * Reachable by keyboard: `⋯` is a real button, and the items are buttons in DOM order. See
 * `MenuSurface` for why that is the bar this clears rather than the full APG menu pattern.
 */
function GoalRowMenu({
  anchor,
  title,
  canNest,
  onClose,
  onEdit,
  onMove,
  onAddChild,
  onDelete,
}: {
  anchor: { x: number; y: number } | null;
  title: string;
  canNest: boolean;
  onClose: () => void;
  onEdit: () => void;
  onMove: () => void;
  onAddChild: () => void;
  onDelete: () => void;
}) {
  const run = (fn: () => void) => () => {
    fn();
    onClose();
  };
  const items: MenuItem[] = [
    { key: 'edit', icon: 'edit', label: 'Edit', run: run(onEdit) },
    { key: 'move', icon: 'next', label: 'Move', run: run(onMove) },
    // Absent rather than disabled at the nesting limit, the same rule the task menu follows.
    ...(canNest
      ? [{ key: 'child', icon: 'plus' as const, label: 'Add sub-goal', run: run(onAddChild) }]
      : []),
    { key: 'd', divider: true },
    { key: 'delete', icon: 'trash', label: 'Delete', danger: true, run: run(onDelete) },
  ];
  return (
    <MenuSurface anchor={anchor} label={`Actions for ${title}`} items={items} onClose={onClose} />
  );
}

/**
 * What the tree knows that a single row cannot.
 *
 * Expansion used to be `useState` inside `GoalNode`, which was the simplest thing that worked
 * until the tree needed a keyboard: a cursor has to walk the rows a person can actually *see*,
 * and nothing above the node knew which those were. So `j` would have jumped into a collapsed
 * subtree — a cursor on a row nobody can see, the exact hazard the list's scroll-into-view fix
 * existed to remove.
 *
 * Expansion therefore lives at the top and rows read it. The default is still per-node
 * (`depth === 0` opens, deeper levels start closed), so the tree looks the same on arrival; only
 * a row somebody has actually toggled carries an entry.
 */
type TreeUi = {
  isExpanded: (node: GoalTreeNode) => boolean;
  toggleExpanded: (node: GoalTreeNode) => void;
  cursorId: string | null;
  setCursor: (id: string) => void;
};

function GoalNode({
  node,
  colors,
  onOpenTask,
  onOpenGoal,
  ui,
}: {
  node: GoalTreeNode;
  /** Colour per goal id, resolved once for the whole tree — see `goalColorMap`. */
  colors: Map<string, string>;
  onOpenTask: (id: string) => void;
  onOpenGoal: (id: string) => void;
  ui: TreeUi;
}) {
  const queryClient = useQueryClient();
  // The day a habit's figure is judged at — a reading taken on it needs no day of its own.
  const { today } = useAccountDay();
  const expanded = ui.isExpanded(node);
  const [showTasks, setShowTasks] = useState(false);
  const [childTitle, setChildTitle] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [moving, setMoving] = useState(false);

  /*
    The rows the count above them counts.

    `includeDone: true` removes the status clause outright, so `dropped` came through with
    everything else — and the count excludes it, by the rule stated at `goalProgress.ts:43`
    ("dropped tasks are not work you failed to do; they leave the denominator"). A goal reading
    "2 tasks" expanded to three rows, and the third rendered exactly like an open one while the
    done task beside it carried a "✓". A count you cannot verify by looking is worse than no count.

    Naming the four statuses rather than adding a dropped marker: the count is the decided rule and
    the list was the accident. A marker would explain a row that has no business being under a
    number that excludes it.
  */
  const listed = node.progress.directTasksOpen + node.progress.directTasksDone;
  const tasks = useQuery({
    queryKey: ['tasks', { goalId: node.goal.id }],
    queryFn: () =>
      listTasks({ goalId: node.goal.id, status: ['inbox', 'next', 'in_progress', 'done'] }),
    enabled: showTasks,
  });

  const addChild = useMutation({
    mutationFn: (title: string) => createGoal({ title, parentGoalId: node.goal.id }),
    onSuccess: () => {
      setChildTitle('');
      setAdding(false);
      void queryClient.invalidateQueries();
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteGoal(node.goal.id),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  /* The way back out of a pause, on the row rather than three clicks into the edit form. */
  const resume = useMutation({
    mutationFn: () => updateGoal(node.goal.id, { status: 'active' }),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  // Per row rather than per tree: the row already owns the state every item drives.
  const menu = useMenuAnchor<string>();

  const hasChildren = node.children.length > 0;
  // Nesting stops at 5 levels (§5.2b), so the control disappears rather than erroring.
  const canNest = node.goal.depth < 4;

  return (
    <li
      className={`goal-node status-${node.goal.status}${
        ui.cursorId === node.goal.id ? ' is-cursor' : ''
      }`}
      data-testid="goal-node"
      data-goal-title={node.goal.title}
      data-cursor={ui.cursorId === node.goal.id || undefined}
    >
      <div
        className="goal-row"
        onContextMenu={(e) => menu.openAt(e, node.goal.id)}
        // Clicking a row puts the cursor on it, so `j` continues from where the eye already is
        // rather than from the top of the tree.
        onMouseDown={() => ui.setCursor(node.goal.id)}
      >
        {/*
          A control where there is something to control, and a spacer where there is not.

          A childless goal rendered a disabled button holding a middle dot: something that looks
          pressable, is announced as a button, and does nothing — on the majority of rows, since
          most goals are leaves. The column still has to hold its width or the tree's left edge
          moves per row, so the space stays and the button goes.

          `aria-expanded` was missing entirely, so the twisty announced only "Collapse"/"Expand"
          as a label and never reported which state it was in. The keys are bound (`h`/`l` and the
          arrows) and were equally unannounced.
        */}
        {hasChildren ? (
          <button
            className="twisty"
            onClick={() => ui.toggleExpanded(node)}
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${node.goal.title}`}
          >
            {expanded ? '▾' : '▸'}
          </button>
        ) : (
          <span className="twisty is-empty" aria-hidden="true" />
        )}

        {/*
          Everything about the goal itself, in the one track allowed to be elastic.

          The row used to be a wrapping flex line with these as loose siblings, so a title long
          enough to push `.goal-trailing` onto a second line handed the bar a whole empty line to
          absorb with its `margin-left: auto` — measured at x=914 against 570 on every other row.
          Grouped here, a long title wraps *inside* its own column and nothing after it moves.

          The paused pill and its "resume" used to render after the bar. They belong to the goal,
          not to its progress, and they are variable-width — left where they were they would have
          been either a second elastic track or a stowaway in the fixed one.
        */}
        <span className="goal-lead">
          {/* The title is the way into the goal's own screen — the tree is for scanning, the
              screen is for working on one thing. */}
          <button className="link goal-title" onClick={() => onOpenGoal(node.goal.id)}>
            {node.goal.title}
            {/*
              The swatch that teaches what the chips on task rows mean. Without it the colours are
              unexplained decoration; with it they are a legend you meet the moment you look at a
              goal.

              Inside the title rather than beside it, so it trails the last word. As a sibling it
              was a second box on the row's one flex line, which put it hard against the progress
              bar and a line below the words whenever a long title wrapped — a floating square
              attached to nothing. It is `aria-hidden`, so it stays out of the button's name.
            */}
            <span className={`goal-swatch ${colors.get(node.goal.id) ?? ''}`} aria-hidden="true" />
          </button>
          {/*
            A paused goal says so, and offers the way back.

            `status: "paused"` rendered as italic type and nothing else — the word appeared nowhere
            on the screen. Pausing is a decision worth seeing, and one people forget they made: the
            goal quietly leaves "Goals today" and the only evidence is a slant.
          */}
          {node.goal.status === 'paused' && (
            <span className="pill warn" title="Paused — its work is not offered on the day">
              paused
            </span>
          )}
          {node.goal.status === 'paused' && (
            <button
              type="button"
              className="link"
              onClick={() => resume.mutate()}
              disabled={resume.isPending}
            >
              resume
            </button>
          )}
        </span>
        {/*
          A goal carried by habits makes no completion claim.

          `computeGoalProgress` weights tasks only, deliberately: a habit never completes, so
          folding one into a percentage would make a goal with all its tasks done read as 60%
          after one bad week — see `GoalHabit` in api.ts. The consequence went unnoticed. A goal
          with *no* task work has a structurally permanent 0%, and printed it beside the habits
          carrying it: "0%" next to "Run distance 27/20 km", which is the opposite of the truth
          with the evidence alongside.
        */}
        {node.progress.totalWeight === 0 && node.habits.length > 0 ? (
          <span className="pill" title="Progress here is the habits below, not tasks finished">
            tracked by habits
          </span>
        ) : (
          <ProgressBar
            percent={node.progress.percent}
            done={node.progress.directTasksDone}
            total={node.progress.directTasksTotal}
            parked={node.progress.directTasksParked}
            rollsUp={node.children.length > 0}
          />
        )}
        {/*
          Everything that follows the bar, in one slot of a fixed width.

          Phase 38 gave the bar `margin-left: auto` so it, its percentage and the actions travel
          as one right-aligned group. That fixed a wider percentage shoving the bar about, and it
          could not fix trailing *siblings*: each row is its own flex line, so the group is pinned
          to the right edge while the bar's own x still depends on what comes after it. A pace
          badge on some rows and not others put the bars 163px apart, and on the architect's
          fifteen goals they sat at 564, 726, 734 and 826.

          Nothing else can fix it. Two rows are two independent flex lines — and two independent
          grids, had we reached for grid — so the only thing that makes the bar land on one x is
          for everything to its right to occupy the same width on every row.
        */}
        <span className="goal-trailing">
          {!(node.progress.totalWeight === 0 && node.habits.length > 0) && (
            <PaceBadge pace={node.pace} />
          )}
          <MomentumBadge momentum={node.momentum} />

          {/*
            Two controls, not five.

            Every goal row carried `n tasks · edit · move · + sub-goal · delete` — five text verbs
            repeated down the whole tree, which is the same weight the task rows were carrying before
            Phase 62 moved their verbs to a command bar. The count stays, because it is information
            as well as the disclosure for the task list; the four verbs move behind `⋯`, which also
            opens on a right-click of the row.
          */}
          <GoalRowMenu
            anchor={menu.state?.subject === node.goal.id ? menu.state.anchor : null}
            title={node.goal.title}
            canNest={canNest}
            onClose={menu.close}
            onEdit={() => setEditing((v) => !v)}
            onMove={() => setMoving((v) => !v)}
            onAddChild={() => setAdding((v) => !v)}
            onDelete={() => {
              const count = 1 + node.children.length;
              if (
                confirm(`Delete "${node.goal.title}" and ${count - 1} sub-goal(s)? Tasks are kept.`)
              )
                remove.mutate();
            }}
          />
          <span className="goal-actions">
            <button className="link" onClick={() => setShowTasks((v) => !v)}>
              {/*
                A phrase, not a bare adjective.

                "0 direct" beside "9%" is two scopes and no nouns: the percentage rolls up the whole
                subtree, the count is this goal's own tasks, and the row said neither. The bar now
                carries "incl. sub-goals" and this says whose tasks it is counting, so the two read
                as different questions rather than as a contradiction.
              */}
              {/*
                The rows the expansion draws, which is open and done: parked is not in that list
                and is not in this number either. This was `directTasksTotal`, so a goal with one
                parked task read "3 tasks" and opened onto two rows. The shelf is named at the foot
                of the expansion instead — on the row it would cost every goal's trailing slot a
                second chip.
              */}
              {listed} {node.children.length > 0 ? 'of its own' : `task${listed === 1 ? '' : 's'}`}
            </button>
            <button
              type="button"
              className="icon-button"
              aria-label={`Actions for ${node.goal.title}`}
              aria-haspopup="menu"
              onClick={(e) => menu.openUnder(e, node.goal.id)}
            >
              <Icon name="more" />
            </button>
          </span>
        </span>
      </div>

      <GoalCallouts goal={node.goal} pace={node.pace} momentum={node.momentum} />

      {/*
        The habits that serve this goal.
        
        Kept out of the percentage on purpose: a habit never completes, so folding one in would
        make a goal with all its tasks done read as 60% because a week dipped. Work finished and
        behaviour maintained are different quantities, and a goal like "run a Spartan trifecta"
        is served almost entirely by the second.
      */}
      {node.habits.length > 0 && (
        <div className="goal-habits" data-testid="goal-habits">
          {node.habits.map((habit) => (
            /*
              The verdict, in the same word and the same tone as every other habit surface.

              This pill was the last one Phase 41 never reached, and it had all three of the
              defects that phase was about. It coloured itself from `onTrack`, which is true for a
              habit with nothing logged in a young period — so a goal's habit went green on the
              absence of evidence, directly under a pace callout that had stopped doing that. Its
              hover text was `habit.state.replace('_', ' ')`, the enum spelled out, which is where
              the retired "on track" survived after `STATE_LABEL` stopped being rendered. And an
              abstinence habit printed the raw state, so a broken one read "breached".
            */
            <span
              key={habit.id}
              className={`pill ${verdictTone(habit.verdict)}`}
              data-testid="goal-habit"
              data-habit-name={habit.name}
              data-verdict={habit.verdict}
              // The period, which the visible text does not say — see the day row's own title.
              title={`${habit.name}: this ${habit.period}`}
            >
              {/*
                The figure from the one formatter — see `habitFigure`. This pill printed its own
                `actual/target unit`: no window, no `amount()` (so *17.900000000000002/18 t*), and
                for a reading nobody took this period *Body fat 0/14 %* (`01a03813-b97d`).
              */}
              {habit.name} {habitFigure(habit, { window: habit.period, asOf: today, today })} ·{' '}
              {/* `verdictWord`, not the bare map: a reading unread for months is *not read this
                  week*, not *too early to tell* (`01a15d62-4f6d`). */}
              {verdictWord(habit, { period: habit.period, asOf: today })}
            </span>
          ))}
        </div>
      )}

      {node.goal.descriptionMd.trim() !== '' && !editing && (
        <p className="goal-why subtext">{node.goal.descriptionMd}</p>
      )}

      {editing && <GoalEditForm node={node} onDone={() => setEditing(false)} />}

      {moving && <MoveGoalForm node={node} onDone={() => setMoving(false)} />}

      {adding && (
        <form
          className="inline-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (childTitle.trim()) addChild.mutate(childTitle.trim());
          }}
        >
          <input
            autoFocus
            value={childTitle}
            placeholder="Sub-goal title"
            onChange={(e) => setChildTitle(e.target.value)}
          />
          <button type="submit">Add</button>
        </form>
      )}

      {showTasks && (
        <ul className="goal-tasks">
          {(tasks.isPending || tasks.isError) && (
            <li>
              <QueryState query={tasks} what="this goal’s tasks" />
            </li>
          )}
          {tasks.data?.length === 0 && node.progress.directTasksParked === 0 && (
            <li className="muted">No tasks on this goal.</li>
          )}
          {tasks.data?.map((task) => (
            <li key={task.id}>
              <button className="link" onClick={() => onOpenTask(task.id)}>
                {task.status === 'done' ? '✓ ' : ''}
                {task.title}
              </button>
            </li>
          ))}
          {node.progress.directTasksParked > 0 && (
            <li className="muted" data-testid="goal-tasks-parked">
              {node.progress.directTasksParked} parked — on the goal’s page
            </li>
          )}
        </ul>
      )}

      {expanded && hasChildren && (
        <ul className="goal-children">
          {node.children.map((child) => (
            <GoalNode
              key={child.goal.id}
              node={child}
              colors={colors}
              onOpenTask={onOpenTask}
              onOpenGoal={onOpenGoal}
              ui={ui}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

/** Counts for the header line, so the state of the whole tree reads at a glance. */
function summarise(nodes: GoalTreeNode[]): { behind: number; stalled: number; total: number } {
  let behind = 0;
  let stalled = 0;
  let total = 0;
  const walk = (list: GoalTreeNode[]) => {
    for (const node of list) {
      total += 1;
      if (node.pace.state === 'behind' || node.pace.state === 'overdue') behind += 1;
      /*
        The same test the chip below is drawn from, so the count and the chips agree.

        This counted `needsNextAction`, which is also true of a goal whose work is *finished* —
        so the banner read "1 goal behind schedule · 2 with no next action" over a page carrying
        exactly one such chip, the second goal being one showing 100%, "on track · 84 days left"
        and a green "ready to finish". A count is a promise about what is on the page.
      */
      if (node.pace.nextActionState === 'not_started') stalled += 1;
      walk(node.children);
    }
  };
  walk(nodes);
  return { behind, stalled, total };
}

/**
 * Where the tree's open rows are remembered, per browser.
 *
 * The same idea as `planner.tasksView` and `planner.tasksSort`: a control that shapes what you
 * are looking at should survive looking at something else. (Not `planner.tasksScope`, which is
 * kept per account because it can name a project — see `SCOPE_KEY` in `TaskList.tsx`. Goal ids
 * here are only ever read for a node being rendered, so another account's are never consulted.) This one forgot on every navigation,
 * which on a five-level tree meant re-opening eight twisties to get back where you were.
 */
const EXPANSION_KEY = 'planner.goalsExpanded';

function readExpansion(): Record<string, boolean> {
  try {
    const saved = JSON.parse(localStorage.getItem(EXPANSION_KEY) ?? '{}') as unknown;
    if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return {};
    // Ids of goals that no longer exist are harmless: `isExpanded` only ever asks about a node it
    // is rendering, so a stale key is never read.
    return Object.fromEntries(
      Object.entries(saved as Record<string, unknown>).filter(([, v]) => typeof v === 'boolean'),
    ) as Record<string, boolean>;
  } catch {
    return {};
  }
}

export function GoalTree({
  onOpenTask,
  onOpenGoal,
}: {
  onOpenTask: (id: string) => void;
  onOpenGoal: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [target, setTarget] = useState('');
  const tree = useQuery({ queryKey: ['goals', 'tree'], queryFn: getGoalTree });
  const colors = useMemo(() => goalColorMap(tree.data ?? []), [tree.data]);

  /*
    Only rows somebody has toggled appear here; everything else falls back to the depth default.

    Remembered per browser, like the tasks screen's scope, sort and view. A five-level hierarchy
    opened with seven of fifteen goals showing and forgot every twisty the moment you opened a
    goal and came back — so the tree was never seen *as* a tree by anyone who used it, only by
    someone who expanded it and then did not click anything.
  */
  const [expansion, setExpansion] = useState<Record<string, boolean>>(readExpansion);
  useEffect(() => {
    localStorage.setItem(EXPANSION_KEY, JSON.stringify(expansion));
  }, [expansion]);

  const isExpanded = useCallback(
    (node: GoalTreeNode) => expansion[node.goal.id] ?? node.goal.depth === 0,
    [expansion],
  );
  const toggleExpanded = useCallback(
    (node: GoalTreeNode) =>
      setExpansion((current) => ({
        ...current,
        [node.goal.id]: !(current[node.goal.id] ?? node.goal.depth === 0),
      })),
    [],
  );

  /*
    Open everything, or shut it.

    Fifteen goals five levels deep is eight twisties to press to see the shape once, and the shape
    is the reason the screen is a tree rather than a list. Written as an explicit map over every
    node rather than a flag, so `isExpanded`'s depth default stays the only fallback and there is
    no second source of truth about what is open.
  */
  const setAllExpanded = useCallback(
    (open: boolean) => {
      const next: Record<string, boolean> = {};
      const walk = (nodes: GoalTreeNode[]) => {
        for (const n of nodes) {
          if (n.children.length > 0) next[n.goal.id] = open;
          walk(n.children);
        }
      };
      walk(tree.data ?? []);
      setExpansion(next);
    },
    [tree.data],
  );

  /** Whether there is a hierarchy at all — no tree, no tree controls. */
  const hasNesting = useMemo(
    () => (tree.data ?? []).some((n) => n.children.length > 0),
    [tree.data],
  );

  /** Whether anything with children is still shut, so the control can say which it does. */
  const anythingCollapsed = useMemo(() => {
    let found = false;
    const walk = (nodes: GoalTreeNode[]) => {
      for (const n of nodes) {
        if (n.children.length > 0 && !isExpanded(n)) found = true;
        walk(n.children);
      }
    };
    walk(tree.data ?? []);
    return found;
  }, [tree.data, isExpanded]);

  /*
    The rows a person can see, in the order they see them — which is what the cursor walks. A
    collapsed goal contributes itself and none of its descendants, so `j` can never land inside a
    subtree that is closed.
  */
  const { visibleIds, byId } = useMemo(() => {
    const ids: string[] = [];
    const index = new Map<string, GoalTreeNode>();
    const walk = (nodes: readonly GoalTreeNode[]) => {
      for (const node of nodes) {
        ids.push(node.goal.id);
        index.set(node.goal.id, node);
        if (isExpanded(node)) walk(node.children);
      }
    };
    walk(tree.data ?? []);
    return { visibleIds: ids, byId: index };
  }, [tree.data, isExpanded]);

  // The list's own cursor machinery, which is generic over ids: it survives a goal being renamed,
  // moved or deleted under it, which a bare index into the tree would not.
  const selection = useTaskSelection(visibleIds);

  const ui: TreeUi = useMemo(
    () => ({
      isExpanded,
      toggleExpanded,
      cursorId: selection.cursorId,
      setCursor: selection.moveTo,
    }),
    [isExpanded, toggleExpanded, selection.cursorId, selection.moveTo],
  );

  useEffect(() => {
    if (selection.cursorId === null) return;
    document.querySelector('[data-cursor]')?.scrollIntoView({ block: 'nearest' });
  }, [selection.cursorId]);

  useKeys('goals', (e: KeyboardEvent) => {
    if (isTypingTarget()) return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;

    if (e.key === 'j' || e.key === 'ArrowDown') {
      e.preventDefault();
      return selection.move(1);
    }
    if (e.key === 'k' || e.key === 'ArrowUp') {
      e.preventDefault();
      return selection.move(-1);
    }

    const node = selection.cursorId === null ? null : (byId.get(selection.cursorId) ?? null);
    if (node === null) return;

    // On a tree the sideways keys are the tree's own: `l` opens what the cursor is standing on
    // so `j` can walk into it, `h` shuts it again. The board spends h/l on columns, but no
    // screen shows both at once.
    if ((e.key === 'l' || e.key === 'ArrowRight') && node.children.length > 0) {
      e.preventDefault();
      if (!isExpanded(node)) toggleExpanded(node);
      return;
    }
    if ((e.key === 'h' || e.key === 'ArrowLeft') && node.children.length > 0) {
      e.preventDefault();
      if (isExpanded(node)) toggleExpanded(node);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      return onOpenGoal(node.goal.id);
    }
  });

  const add = useMutation({
    mutationFn: (t: string) =>
      createGoal({
        title: t,
        ...(target ? { targetDate: new Date(`${target}T12:00:00Z`).toISOString() } : {}),
      }),
    onSuccess: () => {
      setTitle('');
      setTarget('');
      void queryClient.invalidateQueries();
    },
  });

  const summary = tree.data ? summarise(tree.data) : null;

  return (
    <section>
      <form
        /* `goal-capture` so the phone rule that stacks this row cannot reach the eight other
           `.capture` forms, whose shapes stacking would be wrong for — see styles.css. */
        className="capture goal-capture"
        onSubmit={(e) => {
          e.preventDefault();
          if (title.trim()) add.mutate(title.trim());
        }}
      >
        <input
          value={title}
          placeholder="New top-level goal"
          onChange={(e) => setTitle(e.target.value)}
        />
        {/* Offered at creation, because a goal given a deadline on day one is the only
            kind that can be judged on pace at all. */}
        <input
          type="date"
          value={target}
          title="Target date (optional)"
          aria-label="Target date"
          onChange={(e) => setTarget(e.target.value)}
        />
        <button type="submit" disabled={!title.trim()}>
          Add goal
        </button>
      </form>

      {summary && summary.total > 0 && (summary.behind > 0 || summary.stalled > 0) && (
        <p className="banner" data-testid="goal-summary">
          {summary.behind > 0 && (
            <>
              {summary.behind} goal{summary.behind === 1 ? '' : 's'} behind schedule
            </>
          )}
          {summary.behind > 0 && summary.stalled > 0 && ' · '}
          {summary.stalled > 0 && (
            <>
              {summary.stalled} with {NO_NEXT_STEP_LABEL}
            </>
          )}
        </p>
      )}

      <QueryState query={tree} />
      {tree.data?.length === 0 && <p className="muted">No goals yet.</p>}

      {/*
        One press to see the shape.

        Fifteen goals five levels deep opened with seven showing, and the other eight were a
        twisty each — so the hierarchy the screen exists to draw was never actually drawn unless
        somebody worked for it. Shown only when there is a hierarchy to open, and it says which
        way it goes rather than toggling silently between two meanings.
      */}
      {hasNesting && (
        <p className="tree-controls">
          <button
            type="button"
            className="link"
            onClick={() => setAllExpanded(anythingCollapsed)}
            data-testid="expand-all"
          >
            {anythingCollapsed ? 'Open every goal' : 'Collapse to top level'}
          </button>
          {/*
            The tree's own group, not a sentence about two of its keys.

            This hand-wrote `l` and `h` while `?` on the same screen listed j, k, l, h and Enter —
            the two documents `keys.ts` exists to collapse, disagreeing again by three keys on the
            one screen that had never been converted. Same shape as the list's and the board's, so
            the three cannot drift apart independently.
          */}
          <span className="subtext">
            {inlineKeys('goals').map(([key, what]) => (
              <span key={key}>
                {' · '}
                <kbd>{key}</kbd> {what.toLowerCase()}
              </span>
            ))}
          </span>
        </p>
      )}

      <ul className="goal-tree">
        {tree.data?.map((node) => (
          <GoalNode
            key={node.goal.id}
            node={node}
            colors={colors}
            onOpenTask={onOpenTask}
            onOpenGoal={onOpenGoal}
            ui={ui}
          />
        ))}
      </ul>
    </section>
  );
}
